import { InfoCircleOutlined } from '@ant-design/icons'
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined'
import { Spin } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router'
import remarkGfm from 'remark-gfm'
import styled, { keyframes } from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { handleException } from 'clients/exception-handling'
import { useApiClient } from 'clients/yogi-api'
import { buildFeedbackUrl } from 'features/ask-yogi/features/conversation/utils/url-builder'
import { useFeatureFlagGate } from 'features/project/feature-flags'
import { useTopBar } from 'features/project/partials/project-layout/use-layout'
import { useProjectStore } from 'features/project/store'
import {
  AgentMetaName,
  AgentMetaRow,
  CARD_BORDER,
  CardActions,
  CardHeader,
  CardIconButton,
  CardTitle,
  ContentWrapper,
  GlanceColumns,
  GlancePanel,
  GlancePanelDivider,
  InsightDescription,
  InsightHeader,
  InsightTitle,
  PageContainer,
  PageShell,
  renderWithBoldMetrics,
  SECTION_LABEL_COLOR,
  SectionCard,
  TakeawayItem,
  TakeawayList,
  TITLE_COLOR,
} from 'features/trend-detection/components/insight-detail-styles'
import { InsightDetailSubHeader } from 'features/trend-detection/components/InsightDetailSubHeader'
import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'
import { computeGapStrength } from 'features/trend-detection/features/benchmarking-agent/gap-strength-utils'
import { PerformanceBadge } from 'features/trend-detection/features/benchmarking-agent/GapStrengthBadge'
import { VerbatimCard } from 'features/trend-detection/features/benchmarking-agent/VerbatimCard'
import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import {
  trendDetectionBenchmarkingAgentPath,
  trendDetectionFeedPath,
} from 'features/trend-detection/paths'
import type {
  BenchmarkingInsightDetail,
  InsightEntityRow,
  InsightSections,
} from 'features/trend-detection/types'
import { downloadChartAsPng } from 'features/trend-detection/utils/download-chart'
import { formatUtcDateFromISO } from 'features/trend-detection/utils/helpers'
import type { ProjectState } from 'types/project'

// Local const matching the sibling insight-agent pages' INSIGHT_AGENT_FLAG
// shape. There is no shared flag-key module in this tree yet.
const BENCHMARKING_AGENT_FLAG = 'benchmarking_agent'

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseInsightSections(report: string): InsightSections | null {
  try {
    const parsed: unknown = JSON.parse(report)
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'at_a_glance' in parsed
    ) {
      return parsed as InsightSections
    }
    return null
  } catch (cause) {
    handleException(cause, {
      operation: 'agent.insight.report.parse',
      disposition: 'expected',
      isExpectedError: (cause) => cause instanceof SyntaxError,
      reason: 'Legacy report text is not a structured report.',
    })

    return null
  }
}

function computeDirection(entities: InsightEntityRow[]): 'leading' | 'lagging' {
  const focus = entities.find((e) => e.is_focus)
  const outgroup = entities.filter((e) => !e.is_focus)
  if (!focus || outgroup.length === 0) return 'leading'
  const outgroupMean =
    outgroup.reduce((sum, e) => sum + e.avg_sentiment, 0) / outgroup.length
  return focus.avg_sentiment >= outgroupMean ? 'leading' : 'lagging'
}

function formatAnalysisPeriod(period: string): string {
  const match = period.match(/^(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})$/)
  if (match?.[1] && match?.[2]) {
    return `Analysis Period: ${formatUtcDateFromISO(`${match[1]}T00:00:00Z`)} – ${formatUtcDateFromISO(`${match[2]}T00:00:00Z`)}`
  }
  return `Analysis Period: ${period}`
}

// ── Sentiment bar chart ───────────────────────────────────────────────────────

const POSITIVE_GREEN = '#059669'
const NEGATIVE_RED = '#DC2626'
const BAR_HEIGHT = 28
const CHART_FIXED_HEIGHT = 380

const SENTIMENT_AMBER = '#F59E0B'

function getBarColor(value: number): string {
  if (value > 0.3) return POSITIVE_GREEN
  if (value < -0.3) return NEGATIVE_RED
  return SENTIMENT_AMBER
}

const SentimentBarChart: React.FC<{
  entities: InsightEntityRow[]
  wrapRef?: React.RefObject<HTMLDivElement>
  containerRef?: React.RefObject<HTMLDivElement>
  scrollRef?: React.RefObject<HTMLDivElement>
}> = ({ entities, wrapRef, containerRef, scrollRef }) => {
  const focusEntities = entities.filter((e) => e.is_focus)
  const outgroup = entities
    .filter((e) => !e.is_focus)
    .sort((a, b) => b.avg_sentiment - a.avg_sentiment)

  const cohortAvg =
    outgroup.length > 0
      ? outgroup.reduce((sum, e) => sum + e.avg_sentiment, 0) / outgroup.length
      : 0

  // Diverging scale anchored to 0. minVal ≤ 0, maxVal ≥ 0 always.
  const allSentiments = [...entities.map((e) => e.avg_sentiment), cohortAvg]
  const minVal = Math.min(0, ...allSentiments)
  const maxVal = Math.max(0, ...allSentiments)
  const totalSpan = maxVal - minVal || 0.02

  // Fractional positions within the track (0 = left edge, 1 = right edge).
  const zeroFraction = Math.abs(minVal) / totalSpan
  const cohortFraction = (cohortAvg - minVal) / totalSpan
  const hasNegative = minVal < -0.001

  const [hoveredName, setHoveredName] = useState<string | null>(null)
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null)
  const [mousePosition, setMousePosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isDimmed = (name: string): boolean =>
    hoveredName !== null && hoveredName !== name

  const handleRowEnter = (name: string): void => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setHoveredName(name)
    setVisibleTooltip(name)
  }

  const handleRowLeave = (): void => {
    setHoveredName(null)
    hideTimeoutRef.current = setTimeout(() => {
      setVisibleTooltip(null)
    }, 200)
  }

  const handleRowMove = (e: React.MouseEvent): void => {
    setMousePosition({ x: e.clientX, y: e.clientY })
  }

  const renderRow = (entity: InsightEntityRow): React.ReactElement => {
    const v = entity.avg_sentiment
    const barWidthPct = (Math.abs(v) / totalSpan) * 100
    const barLeftPct =
      v >= 0 ? zeroFraction * 100 : ((v - minVal) / totalSpan) * 100
    const color = getBarColor(v)
    const dimmed = isDimmed(entity.name)
    return (
      <BarRow
        key={entity.name}
        onMouseEnter={() => handleRowEnter(entity.name)}
        onMouseLeave={handleRowLeave}
        onMouseMove={handleRowMove}
      >
        <BarLabel $isFocus={entity.is_focus} $dimmed={dimmed}>
          {entity.name}
        </BarLabel>
        <BarTrackArea>
          <BarTrack>
            <BarFill
              $leftPct={barLeftPct}
              $widthPct={barWidthPct}
              $color={color}
              $opacity={entity.is_focus ? 0.9 : 0.72}
              $negative={v < 0}
              $dimmed={dimmed}
            />
          </BarTrack>
        </BarTrackArea>
        <BarValueLabel $dimmed={dimmed}>{v.toFixed(2)}</BarValueLabel>
      </BarRow>
    )
  }

  const hoveredEntity =
    visibleTooltip != null
      ? entities.find((e) => e.name === visibleTooltip)
      : null

  return (
    <BarChartWrap ref={wrapRef}>
      <BarChartLegend>
        <BarChartLegendDash />
        Cohort average ({cohortAvg.toFixed(2)})
      </BarChartLegend>
      <ChartContainer ref={containerRef}>
        <FocusPinnedRow>{focusEntities.map(renderRow)}</FocusPinnedRow>
        <ChartScrollArea ref={scrollRef}>
          {outgroup.map(renderRow)}
        </ChartScrollArea>
        {hasNegative && <ZeroLineOverlay $fraction={zeroFraction} />}
        <CohortLineOverlay $fraction={cohortFraction} />
      </ChartContainer>
      {hoveredEntity &&
        visibleTooltip &&
        mousePosition &&
        (() => {
          const offset = 15
          const ttWidth = tooltipRef.current?.offsetWidth ?? 240
          const ttHeight = tooltipRef.current?.offsetHeight ?? 140

          let tooltipX = mousePosition.x + offset
          let tooltipY = mousePosition.y + offset
          if (tooltipX + ttWidth > window.innerWidth) {
            tooltipX = mousePosition.x - ttWidth - offset
          }
          if (tooltipY + ttHeight > window.innerHeight) {
            tooltipY = mousePosition.y - ttHeight - offset
          }
          tooltipX = Math.max(
            0,
            Math.min(tooltipX, window.innerWidth - ttWidth),
          )
          tooltipY = Math.max(
            0,
            Math.min(tooltipY, window.innerHeight - ttHeight),
          )

          const isLeaving = hoveredName !== visibleTooltip
          const v = hoveredEntity.avg_sentiment
          const recordCount =
            hoveredEntity.record_count ?? hoveredEntity.review_count
          const delta = v - cohortAvg
          const deltaSign = delta >= 0 ? '+' : ''

          return (
            <BarChartTooltip
              ref={tooltipRef}
              style={{ left: tooltipX, top: tooltipY }}
              $leaving={isLeaving}
            >
              <BarChartTooltipHeader>
                <BarChartTooltipSwatch style={{ background: getBarColor(v) }} />
                <BarChartTooltipTitle>
                  {hoveredEntity.is_focus
                    ? `${hoveredEntity.name} (focus)`
                    : hoveredEntity.name}
                </BarChartTooltipTitle>
              </BarChartTooltipHeader>
              <BarChartTooltipRow>Sentiment: {v.toFixed(2)}</BarChartTooltipRow>
              {recordCount != null && (
                <BarChartTooltipRow>
                  # of Records: {recordCount.toLocaleString()}
                </BarChartTooltipRow>
              )}
              <BarChartTooltipRow>
                vs. cohort avg: {deltaSign}
                {delta.toFixed(2)}
              </BarChartTooltipRow>
            </BarChartTooltip>
          )
        })()}
    </BarChartWrap>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export const BenchmarkingInsightDetailPage: React.FC = () => {
  const { insightId } = useParams<{ insightId: string }>()
  const projectId = useProjectStore((s: ProjectState) => s.projectId)
  const apiClient = useApiClient()
  const navigate = useNavigate()
  const { setBreadcrumbSubtitle } = useTopBar()
  // Redirects only once the flags request RESOLVES to "off" — see the sibling
  // agent-detail page for the race the previous inline effect had.
  useFeatureFlagGate(
    BENCHMARKING_AGENT_FLAG,
    projectId ? trendDetectionFeedPath(projectId) : '/',
  )

  const { data: insight, isLoading } = useQuery<BenchmarkingInsightDetail>({
    queryKey: ['benchmarking-insight', projectId, insightId],
    queryFn: async () => {
      if (!projectId || !insightId) throw new Error('missing')
      const { data } = await apiClient.get(
        `/benchmarking/insights/${insightId}?proj_uuid=${projectId}`,
      )
      return data as BenchmarkingInsightDetail
    },
    enabled: !!projectId && !!insightId,
    refetchOnWindowFocus: false,
  })

  const benchmarkingViewFiredRef = useRef(false)
  useEffect(() => {
    if (!insightId || !insight || benchmarkingViewFiredRef.current) return
    benchmarkingViewFiredRef.current = true
    trackResearchAgents(ResearchAgentsAction.benchmarking_insight_view, {
      insight_uuid: insightId,
      insight_title: insight.title,
      agent_uuid: insight.agent_uuid,
      agent_name: insight.agent_name,
    })
  }, [insightId, insight])

  useEffect(() => {
    setBreadcrumbSubtitle(insight?.title ?? null)
    return () => setBreadcrumbSubtitle(null)
  }, [insight?.title, setBreadcrumbSubtitle])

  const pageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let el = pageRef.current?.parentElement
    while (el) {
      const { overflowY } = window.getComputedStyle(el)
      if (overflowY === 'auto' || overflowY === 'scroll') {
        el.scrollTop = 0
        break
      }
      el = el.parentElement
    }
  }, [])

  const chartWrapRef = useRef<HTMLDivElement>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartScrollRef = useRef<HTMLDivElement>(null)

  const handleDownload = useCallback(async () => {
    const wrap = chartWrapRef.current
    const container = chartContainerRef.current
    const scroll = chartScrollRef.current
    if (!wrap || !container || !scroll) return

    // Lift the container's max-height and expand the scroll area so all rows are captured
    const prevContainerMaxHeight = container.style.maxHeight
    const prevOverflow = scroll.style.overflowY
    const prevHeight = scroll.style.height
    container.style.maxHeight = 'none'
    scroll.style.overflowY = 'visible'
    scroll.style.height = 'auto'

    try {
      await downloadChartAsPng(wrap, insight?.title ?? 'sentiment-chart')
      trackResearchAgents(ResearchAgentsAction.insight_chart_download, {
        chart: 'sentiment_comparison',
        source: 'benchmarking_insight',
      })
    } finally {
      container.style.maxHeight = prevContainerMaxHeight
      scroll.style.overflowY = prevOverflow
      scroll.style.height = prevHeight
    }
  }, [insight?.title])

  if (!projectId || !insightId) return null

  const sections = insight ? parseInsightSections(insight.report) : null
  const direction =
    sections?.where_you_stand?.entities != null
      ? computeDirection(sections.where_you_stand.entities)
      : 'leading'
  const gapStrength = computeGapStrength(insight?.z_score ?? sections?.z_score)
  const focusEntityName =
    sections?.where_you_stand?.entities?.find((e) => e.is_focus)?.name ?? null

  return (
    <>
      <PageShell>
        <InsightDetailSubHeader
          onBack={() => navigate(trendDetectionFeedPath(projectId))}
          backLabel="Back to Feed"
          feedbackDisabled
          trackingDisabled
          onShare={() =>
            trackResearchAgents(ResearchAgentsAction.insight_share_click, {
              source: 'benchmarking_insight',
            })
          }
        />
        <PageContainer ref={pageRef}>
          <ContentWrapper>
            {isLoading && (
              <LoadingSection>
                <Spin size="large" />
              </LoadingSection>
            )}

            {!isLoading && insight && (
              <>
                {/* ── Insight header ─────────────────────────────────────────── */}
                <InsightHeader>
                  <PerformanceBadge
                    direction={direction}
                    strength={gapStrength}
                  />

                  <InsightTitle>
                    {insight.title ?? 'Benchmarking Insight'}
                  </InsightTitle>

                  {sections?.at_a_glance?.summary && (
                    <InsightDescription>
                      {sections.at_a_glance.summary}
                    </InsightDescription>
                  )}

                  <AgentMetaRow>
                    <AgentMetaName
                      onClick={() =>
                        navigate(
                          trendDetectionBenchmarkingAgentPath(
                            projectId,
                            insight.agent_uuid,
                          ),
                        )
                      }
                    >
                      {insight.agent_name}
                    </AgentMetaName>
                    {sections?.analysis_period && (
                      <> · {formatAnalysisPeriod(sections.analysis_period)}</>
                    )}
                  </AgentMetaRow>
                </InsightHeader>

                {sections ? (
                  <>
                    {/* ── Section 1: Key findings + Current signals ───────────── */}
                    <SectionCard>
                      <GlanceColumns>
                        <GlancePanel>
                          <CardTitle>Key findings</CardTitle>
                          <TakeawayList>
                            {sections.at_a_glance.takeaways.map((t, i) => (
                              <TakeawayItem key={i}>{t}</TakeawayItem>
                            ))}
                          </TakeawayList>
                        </GlancePanel>

                        <GlancePanelDivider />

                        <GlancePanel>
                          <CardTitle>Current signals</CardTitle>
                          <TakeawayList>
                            {sections.at_a_glance.key_signals.map((s, i) => (
                              <TakeawayItem key={i}>
                                {renderWithBoldMetrics(s)}
                              </TakeawayItem>
                            ))}
                          </TakeawayList>
                        </GlancePanel>
                      </GlanceColumns>
                    </SectionCard>

                    {/* ── Section 2: Where you stand ──────────────────────────── */}
                    <SectionCard>
                      <CardHeader>
                        <CardTitle>
                          How you compare on sentiment
                          <Tooltip content="How positive or negative customer language is for a topic, on a scale of -1 to +1.">
                            <InfoCircleOutlined
                              style={{
                                fontSize: 13,
                                marginLeft: 6,
                                color: '#717680',
                                cursor: 'default',
                                verticalAlign: 'middle',
                              }}
                            />
                          </Tooltip>
                        </CardTitle>
                        <CardActions>
                          <CardIconButton
                            title="Download as PNG"
                            onClick={handleDownload}
                          >
                            <FileDownloadOutlined style={{ fontSize: 14 }} />
                          </CardIconButton>
                        </CardActions>
                      </CardHeader>
                      <SentimentBarChart
                        entities={sections.where_you_stand.entities}
                        wrapRef={chartWrapRef}
                        containerRef={chartContainerRef}
                        scrollRef={chartScrollRef}
                      />
                    </SectionCard>

                    {/* ── Section 3: What's driving this ─────────────────────── */}
                    <SectionCard>
                      <CardTitle>
                        What customers talk about
                        <Tooltip content="What customers raise about your benchmark focus vs. your cohort. Percentages show each theme's share of that feedback.">
                          <InfoCircleOutlined
                            style={{
                              fontSize: 13,
                              marginLeft: 6,
                              color: '#717680',
                              cursor: 'default',
                              verticalAlign: 'middle',
                            }}
                          />
                        </Tooltip>
                      </CardTitle>
                      <ThemeList>
                        {sections.whats_driving_it.themes.map((t) => {
                          const matchingQuote = sections.in_their_words.find(
                            (v) =>
                              v.theme?.toLowerCase() ===
                              t.theme_name?.toLowerCase(),
                          )
                          const tooltipLines = [
                            t.focus_sentiment != null &&
                              (t.outgroup_sentiment ?? t.cohort_sentiment) !=
                                null &&
                              `Sentiment: ${t.focus_sentiment.toFixed(2)} vs. cohort ${(t.outgroup_sentiment ?? t.cohort_sentiment)!.toFixed(2)}`,
                            (t.outgroup_share_pct ?? t.cohort_share_pct) !=
                              null &&
                              `Share: ${Math.round(t.focus_share_pct)}% vs. cohort ${Math.round((t.outgroup_share_pct ?? t.cohort_share_pct)!)}%`,
                          ].filter(Boolean)
                          return (
                            <ThemeCard key={t.theme_name}>
                              <ThemeCardHeader>
                                <ThemeCardHeaderLeft>
                                  <ThemeName>{t.theme_name}</ThemeName>
                                  <Tooltip
                                    content={
                                      tooltipLines.length > 0
                                        ? tooltipLines.join(' · ')
                                        : undefined
                                    }
                                  >
                                    <ThemeDirectionTag
                                      $direction={t.direction ?? 'neutral'}
                                    >
                                      {t.direction === 'behind'
                                        ? '↓ Behind cohort'
                                        : t.direction === 'ahead'
                                          ? '↑ Ahead of cohort'
                                          : '≈ On par with cohort'}
                                    </ThemeDirectionTag>
                                  </Tooltip>
                                </ThemeCardHeaderLeft>
                                <ThemeShareStat>
                                  {Math.round(t.focus_share_pct)}% of feedback
                                </ThemeShareStat>
                              </ThemeCardHeader>
                              <ThemeExplanation>
                                {t.explanation ?? t.insight}
                              </ThemeExplanation>
                              {matchingQuote && (
                                <VerbatimCard
                                  verbatim={matchingQuote}
                                  projectId={projectId}
                                />
                              )}
                            </ThemeCard>
                          )
                        })}
                      </ThemeList>
                    </SectionCard>

                    <PageFooterLink
                      href={
                        insight.feedback_filter_hash && projectId
                          ? buildFeedbackUrl(
                              projectId,
                              insight.feedback_filter_hash,
                            )
                          : '#'
                      }
                      target={
                        insight.feedback_filter_hash ? '_blank' : undefined
                      }
                      rel={
                        insight.feedback_filter_hash ? 'noreferrer' : undefined
                      }
                      onClick={
                        !insight.feedback_filter_hash || !projectId
                          ? (e) => e.preventDefault()
                          : () =>
                              trackResearchAgents(
                                ResearchAgentsAction.view_all_records_click,
                                { source: 'benchmarking_insight' },
                              )
                      }
                    >
                      View all records
                      {focusEntityName ? ` for ${focusEntityName}` : ''} →
                    </PageFooterLink>
                  </>
                ) : (
                  /* Legacy markdown fallback */
                  <ReportCard>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {insight.report}
                    </ReactMarkdown>
                  </ReportCard>
                )}
              </>
            )}
          </ContentWrapper>
        </PageContainer>
      </PageShell>
    </>
  )
}

// ── Page-specific styled components ──────────────────────────────────────────

const PageFooterLink = styled.a`
  display: inline-flex;
  align-items: center;
  font-size: 13.5px;
  font-weight: 600;
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  text-decoration: none;
  align-self: flex-start;
  margin-bottom: 56px;

  &:hover {
    text-decoration: underline;
  }
`

const LoadingSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
`

// ── Sentiment bar chart styled components ─────────────────────────────────────

const BarChartWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const BarChartLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: ${SECTION_LABEL_COLOR};
  align-self: flex-end;
`

const BarChartLegendDash = styled.span`
  width: 18px;
  border-top: 2px dashed #9ca3af;
  display: inline-block;
`

const ChartContainer = styled.div`
  max-height: ${CHART_FIXED_HEIGHT}px;
  display: flex;
  flex-direction: column;
  position: relative;
`

const FocusPinnedRow = styled.div`
  flex-shrink: 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow:
    0 2px 10px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 4px 0;
  margin-left: -12px;
  padding-left: 12px;
  margin-bottom: 6px;
`

const ChartScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
`

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  cursor: default;
`

const BarLabel = styled.div<{ $isFocus?: boolean; $dimmed?: boolean }>`
  width: 190px;
  flex-shrink: 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${TITLE_COLOR};
  font-weight: ${(p) => (p.$isFocus ? 700 : 400)};
  opacity: ${(p) => (p.$dimmed ? 0.3 : 1)};
  transition: opacity 0.15s ease;
`

const BarTrackArea = styled.div`
  flex: 1;
  height: ${BAR_HEIGHT}px;
  position: relative;
`

const BarTrack = styled.div`
  width: 100%;
  height: 100%;
  background: #f3f4f6;
  border-radius: 0 4px 4px 0;
  position: relative;
  overflow: hidden;
`

const BarFill = styled.div<{
  $leftPct: number
  $widthPct: number
  $color: string
  $opacity: number
  $negative: boolean
  $dimmed?: boolean
}>`
  position: absolute;
  top: 0;
  height: 100%;
  left: ${(p) => p.$leftPct}%;
  width: ${(p) => p.$widthPct}%;
  background: ${(p) => p.$color};
  opacity: ${(p) => (p.$dimmed ? 0.15 : p.$opacity)};
  border-radius: ${(p) => (p.$negative ? '3px 0 0 3px' : '0 3px 3px 0')};
  transition: opacity 0.15s ease;
`

// left = row_padding (4px) + label_col (190px) + gap (12px)
//      + fraction × track_width (100% − 258px)
const ZeroLineOverlay = styled.div<{ $fraction: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(205px + ${(p) => p.$fraction.toFixed(4)} * (100% - 258px));
  width: 0;
  border-left: 1.5px solid #6b7280;
  pointer-events: none;
  z-index: 1;

  &::before {
    content: '0';
    position: absolute;
    top: -16px;
    left: -6px;
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
  }
`

const CohortLineOverlay = styled.div<{ $fraction: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(206px + ${(p) => p.$fraction.toFixed(4)} * (100% - 258px));
  width: 0;
  border-left: 2px dashed #9ca3af;
  pointer-events: none;
  z-index: 2;
`

const BarValueLabel = styled.span<{ $dimmed?: boolean }>`
  flex-shrink: 0;
  width: 36px;
  font-size: 11.5px;
  color: ${SECTION_LABEL_COLOR};
  text-align: right;
  opacity: ${(p) => (p.$dimmed ? 0.3 : 1)};
  transition: opacity 0.15s ease;
`

const tooltipFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`

const tooltipFadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-4px); }
`

const BarChartTooltip = styled.div<{ $leaving: boolean }>`
  position: fixed;
  background-color: #2d3748;
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 1000;
  font-size: 13px;
  line-height: 1.6;
  min-width: 220px;
  opacity: 0;
  animation: ${(p) => (p.$leaving ? tooltipFadeOut : tooltipFadeIn)} 0.2s
    ease-out forwards;
`

const BarChartTooltipHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`

const BarChartTooltipSwatch = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 8px;
  flex-shrink: 0;
`

const BarChartTooltipTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`

const BarChartTooltipRow = styled.div`
  margin-bottom: 4px;
  color: #e2e8f0;

  &:last-child {
    margin-bottom: 0;
  }
`

// ── Theme block styled components ─────────────────────────────────────────────

const ThemeList = styled.div`
  display: flex;
  flex-direction: column;
`

const ThemeCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid ${CARD_BORDER};

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`

const ThemeCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const ThemeCardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const ThemeName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${TITLE_COLOR};
`

const ThemeDirectionTag = styled.span<{
  $direction: 'ahead' | 'behind' | 'neutral'
}>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11.5px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${(p) =>
    p.$direction === 'behind'
      ? NEGATIVE_RED
      : p.$direction === 'ahead'
        ? POSITIVE_GREEN
        : SECTION_LABEL_COLOR};
  background: ${(p) =>
    p.$direction === 'behind'
      ? 'rgba(220,38,38,0.08)'
      : p.$direction === 'ahead'
        ? 'rgba(5,150,105,0.08)'
        : 'rgba(113,118,128,0.08)'};
`

const ThemeShareStat = styled.span`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${SECTION_LABEL_COLOR};
  white-space: nowrap;
`

const ThemeExplanation = styled.p`
  font-size: 13.5px;
  color: #6c737d;
  line-height: 1.6;
  margin: 0;
`

// ── Legacy markdown fallback ──────────────────────────────────────────────────

const ReportCard = styled.div`
  background: white;
  border: 1px solid ${CARD_BORDER};
  border-radius: 20px;
  padding: 28px 32px;
`
